import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useConfirmLoginMutation } from '../features/auth/passwordSlice'
import toast from 'react-hot-toast'

export const LoginConfirm = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [confirmLogin, { isLoading }] = useConfirmLoginMutation()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')

  useEffect(() => {
    const token = searchParams.get('token')
    
    if (!token) {
      toast.error('Invalid confirmation token')
      navigate('/login')
      return
    }

    // Immediately call confirm login API
    handleConfirmLogin(token)
  }, [])

  const handleConfirmLogin = async (token: string) => {
    try {
      toast.loading('Confirming login...')
      await confirmLogin({ token }).unwrap()
      toast.dismiss()
      
      setStatus('success')
      toast.success('Login confirmed successfully!', {
        duration: 2000,
        icon: '✅',
      })

      // Redirect to app home after 2 seconds
      setTimeout(() => {
        navigate('/app/home')
      }, 2000)
    } catch (error: any) {
      toast.dismiss()
      setStatus('error')
      const errorMessage = error?.data?.message || 'Failed to confirm login'
      toast.error(errorMessage)
      
      // Redirect to login on error
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    }
  }

  return (
    <div className='login-bg1 w-full font-dashboard bg-[#f8f8f8] h-screen flex justify-center items-center'>
      <div className='bg-white w-full max-w-[400px] rounded-md shadow-lg px-5 py-10'>
        <h3 className='text-2xl text-indigo-500 font-semibold text-center'>
          Gcbuying
        </h3>
        
        {status === 'loading' && (
          <div className='mt-8 text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500'></div>
            <p className='text-sm text-slate-600 mt-4'>Confirming login...</p>
          </div>
        )}

        {status === 'success' && (
          <div className='mt-8 text-center'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4'>
              <svg
                className='w-8 h-8 text-green-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M5 13l4 4L19 7'
                />
              </svg>
            </div>
            <h4 className='text-lg font-[500] text-slate-700 mb-2'>
              Login Confirmed ✅
            </h4>
            <p className='text-sm text-slate-500'>
              This login was from you. Redirecting to home...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className='mt-8 text-center'>
            <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4'>
              <svg
                className='w-8 h-8 text-red-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </div>
            <h4 className='text-lg font-[500] text-slate-700 mb-2'>
              Confirmation Failed
            </h4>
            <p className='text-sm text-slate-500'>
              Invalid or expired token. Redirecting to login...
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

